import { useState, useRef, useCallback, useEffect } from 'react';
import { RoundProps } from './types';
import AdultHelp from './AdultHelp';

function Instruction({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl px-5 py-3 text-center w-full max-w-md mx-auto">
      <p className="text-base font-extrabold text-amber-800">{text}</p>
      {sub && <p className="text-sm text-amber-600 mt-1 italic">{sub}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 7: NHÓM LỬA
// ══════════════════════════════════════════════════════
const WOOD_PIECES = [
  { id: 1, emoji: '🪵', label: 'Củi 1' },
  { id: 2, emoji: '🪵', label: 'Củi 2' },
  { id: 3, emoji: '🪵', label: 'Củi 3' },
];

export function Round5NhomLua({ onComplete }: RoundProps) {
  const [phase, setPhase] = useState<'arrange' | 'adult-help' | 'child-blow' | 'done'>('arrange');
  const [woodInStove, setWoodInStove] = useState<number[]>([]);
  const [draggingWood, setDraggingWood] = useState<number | null>(null);
  const [blowPower, setBlowPower] = useState(0);
  const [charcoalHeat, setCharcoalHeat] = useState(0);
  const blowRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const decayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const remainingWood = WOOD_PIECES.filter(w => !woodInStove.includes(w.id));

  const handleWoodDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggingWood === null) return;
    setWoodInStove(prev => {
      const next = [...prev, draggingWood];
      if (next.length >= WOOD_PIECES.length) {
        setTimeout(() => setPhase('adult-help'), 600);
      }
      return next;
    });
    setDraggingWood(null);
  };

  const startBlow = useCallback(() => {
    if (decayRef.current) clearInterval(decayRef.current);
    blowRef.current = setInterval(() => {
      setBlowPower(v => Math.min(100, v + 3));
      setCharcoalHeat(v => {
        const next = Math.min(100, v + 2);
        if (next >= 90) {
          if (blowRef.current) clearInterval(blowRef.current);
          setPhase('done');
          setTimeout(() => onComplete(true, 90), 800);
        }
        return next;
      });
    }, 100);
  }, [onComplete]);

  const stopBlow = useCallback(() => {
    setBlowPower(0);
    if (blowRef.current) clearInterval(blowRef.current);
    decayRef.current = setInterval(() => {
      setCharcoalHeat(v => Math.max(0, v - 1.2));
    }, 200);
  }, []);

  const charcoalColor = charcoalHeat < 20 ? '#555' : charcoalHeat < 50 ? '#7c3a1a' : charcoalHeat < 80 ? '#b45309' : '#dc2626';

  if (phase === 'arrange') {
    return (
      <div className="flex flex-col items-center gap-5 p-6 bg-red-50 rounded-3xl min-h-80">
        <h3 className="text-2xl font-extrabold text-red-700 text-center">🔥 Nhóm lửa!</h3>
        <Instruction text="👆 Kéo thả từng thanh củi vào bếp để xếp củi!" sub="Xếp hết 3 thanh củi trước khi nhờ người lớn châm lửa" />
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-3 text-center">
          <p className="text-base font-extrabold text-orange-700">⚠️ Lửa rất nguy hiểm! Chỉ xếp củi thôi, nhờ người lớn châm lửa!</p>
        </div>
        <div className="flex gap-5">
          {remainingWood.map(w => (
            <div key={w.id} draggable
              onDragStart={() => setDraggingWood(w.id)}
              onDragEnd={() => setDraggingWood(null)}
              className={`flex flex-col items-center gap-2 cursor-grab active:cursor-grabbing select-none transition-all ${draggingWood === w.id ? 'opacity-30' : 'hover:scale-110 animate-bounce'}`}>
              <span className="text-5xl">{w.emoji}</span>
              <span className="text-sm text-amber-700 font-extrabold">Kéo vào bếp</span>
            </div>
          ))}
        </div>
        <div onDragOver={e => e.preventDefault()} onDrop={handleWoodDrop}
          className="w-full max-w-md h-28 rounded-2xl border-4 border-dashed border-red-400 flex flex-col items-center justify-center bg-red-50 transition-all hover:border-red-600">
          <span className="text-3xl">🏕️</span>
          <p className="text-base font-extrabold text-red-600">Thả củi vào bếp ({woodInStove.length}/{WOOD_PIECES.length})</p>
          {woodInStove.length > 0 && (
            <div className="flex gap-2 mt-1">
              {woodInStove.map(id => <span key={id} className="text-3xl">🪵</span>)}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'adult-help') {
    return (
      <div className="flex flex-col gap-5 p-6 bg-red-50 rounded-3xl min-h-80">
        <h3 className="text-2xl font-extrabold text-red-700 text-center">🔥 Nhờ người lớn châm lửa!</h3>
        <div className="flex justify-center gap-2">
          {WOOD_PIECES.map(w => <span key={w.id} className="text-4xl">{w.emoji}</span>)}
          <span className="text-4xl">🏕️</span>
        </div>
        <AdultHelp
          dangerousTask="Châm lửa vào củi"
          adultAction="Người lớn đang cẩn thận châm lửa vào củi..."
          childSafeTask="Em thổi nhẹ để giúp than đỏ lên nhé!"
          adultEmoji="👨‍🌾"
          onAdultDone={() => setPhase('child-blow')}
        />
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="flex flex-col items-center justify-center min-h-80 p-7 bg-orange-50 rounded-3xl gap-6">
        <div className="text-8xl">♨️</div>
        <p className="text-2xl font-extrabold text-green-600 text-center">✅ Than đã đỏ rực!</p>
        <p className="text-lg text-orange-600 text-center">Sẵn sàng nướng cơm lam rồi!</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-80 p-6 bg-red-50 rounded-3xl gap-5">
      <h3 className="text-2xl font-extrabold text-red-700 text-center">💨 Thổi lửa cho than đỏ!</h3>
      <Instruction text="🖱️ Nhấn giữ nút để thổi nhẹ và đều vào bếp!" sub="Thổi quá mạnh → lửa tắt! Thổi vừa đủ thôi!" />
      <div className="flex gap-3 h-10 justify-center">
        {Array.from({ length: Math.floor(charcoalHeat / 25) }).map((_, i) => (
          <span key={i} className="text-2xl animate-bounce" style={{ animationDelay: `${i * 0.2}s` }}>🔥</span>
        ))}
      </div>
      <div className="flex gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="w-12 h-10 rounded-lg transition-all duration-300"
            style={{ backgroundColor: charcoalColor, boxShadow: charcoalHeat > 50 ? `0 0 ${charcoalHeat / 5}px ${charcoalColor}` : 'none' }} />
        ))}
      </div>
      <div className="w-full max-w-sm">
        <div className="flex justify-between text-sm mb-2 font-bold">
          <span className="text-gray-400">Lạnh</span>
          <span className="font-extrabold text-base" style={{ color: charcoalColor }}>Than: {Math.round(charcoalHeat)}%</span>
          <span className="text-red-500">Đỏ rực</span>
        </div>
        <div className="w-full h-5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${charcoalHeat}%`, background: 'linear-gradient(to right, #78350f, #dc2626)' }} />
        </div>
      </div>
      <button
        onMouseDown={startBlow} onMouseUp={stopBlow} onMouseLeave={stopBlow}
        onTouchStart={startBlow} onTouchEnd={stopBlow}
        className={`px-12 py-5 text-white font-extrabold text-xl rounded-2xl cursor-pointer select-none transition-all whitespace-nowrap ${blowPower > 0 ? 'bg-orange-400 scale-95' : 'bg-red-500 hover:bg-red-600'}`}>
        💨 GIỮ nút để thổi lửa
      </button>
      <p className="text-base text-red-500 text-center font-semibold">Thổi nhẹ từ từ thôi — không thổi mạnh quá!</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 8: NƯỚNG CƠM - GIỮ LỬA
// ══════════════════════════════════════════════════════
export function Round6NuongCom({ onComplete, cookedLevel, setCookedLevel }: RoundProps & { cookedLevel: number; setCookedLevel: (v: number) => void }) {
  const [heat, setHeat] = useState(30);
  const [cookProgress, setCookProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [fuelCount, setFuelCount] = useState(2);
  const [consequence, setConsequence] = useState('');

  const MIN_FUEL = 0;
  const MAX_FUEL = 6;

  useEffect(() => {
    if (done) return;
    const iv = setInterval(() => {
      setHeat(h => {
        const target = fuelCount * 16;
        const diff = target - h;
        const next = h + diff * 0.20 + (Math.random() - 0.5) * 2;
        return Math.max(5, Math.min(100, next));
      });
    }, 150);
    return () => clearInterval(iv);
  }, [done, fuelCount]);

  useEffect(() => {
    if (done) return;
    const inRange = heat >= 38 && heat <= 72;
    if (inRange) {
      setCookProgress(v => {
        const next = Math.min(100, v + 0.6);
        if (next >= 100) {
          setDone(true);
          setCookedLevel(Math.min(100, cookedLevel + 50));
          setTimeout(() => onComplete(true, 100), 800);
        }
        return next;
      });
    } else if (heat > 85) {
      setConsequence('🔥 Quá nóng! Bớt củi đi!');
      setTimeout(() => setConsequence(''), 2000);
    } else if (heat < 25) {
      setConsequence('❄️ Lửa quá nhỏ! Thêm củi vào!');
      setTimeout(() => setConsequence(''), 2000);
    }
  }, [heat, done, cookedLevel, setCookedLevel, onComplete]);

  const heatZone = heat < 38 ? 'cold' : heat <= 72 ? 'perfect' : 'hot';
  const heatColor = heatZone === 'perfect' ? '#22c55e' : heatZone === 'hot' ? '#ef4444' : '#60a5fa';
  const tubeColor = cookProgress > 75 ? '#92400e' : cookProgress > 45 ? '#b45309' : '#d97706';
  const flameCount = Math.max(0, Math.min(5, Math.floor(heat / 20)));

  return (
    <div className="flex flex-col items-center gap-4 p-5 bg-orange-50 rounded-3xl min-h-96">
      <h3 className="text-2xl font-extrabold text-orange-700 text-center">🌡️ Nướng cơm — Giữ lửa vừa đủ!</h3>
      <Instruction text="👇 Bấm NÚT để thêm củi hoặc bớt củi, giữ ngọn lửa ở vùng XANH LÁ!" sub="Lửa xanh lá = vừa đủ → cơm chín đều" />

      {consequence && (
        <div className="bg-red-100 border-2 border-red-300 rounded-2xl px-5 py-3 text-lg font-extrabold text-red-700 animate-bounce text-center">
          {consequence}
        </div>
      )}

      <div className="flex flex-col items-center gap-3 w-full max-w-sm">
        <div className="flex flex-col items-center gap-2">
          <div className="w-16 h-36 rounded-xl border-2 border-amber-400 transition-all duration-700 flex items-center justify-center"
            style={{ backgroundColor: tubeColor, boxShadow: `0 0 ${heat / 8}px ${heatColor}` }}>
            <span className="text-3xl">🎍</span>
          </div>
          <p className="text-base font-extrabold" style={{ color: tubeColor }}>
            {cookProgress > 75 ? '🟫 Vàng thơm!' : cookProgress > 45 ? '🟡 Đang chín' : '⬜ Còn trắng'}
          </p>
        </div>

        <div className="flex gap-2 justify-center h-10 items-end">
          {Array.from({ length: flameCount }).map((_, i) => (
            <span key={i} className="text-3xl animate-bounce" style={{ animationDelay: `${i * 0.2}s` }}>🔥</span>
          ))}
          {flameCount === 0 && <span className="text-base text-gray-400 font-semibold">— Lửa tắt —</span>}
        </div>

        <div className="flex gap-2 justify-center min-h-8">
          {Array.from({ length: fuelCount }).map((_, i) => (
            <span key={i} className="text-2xl">🪵</span>
          ))}
          {fuelCount === 0 && <span className="text-sm text-gray-400">Chưa có củi</span>}
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-5 mt-2">
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => setFuelCount(v => Math.max(MIN_FUEL, v - 1))}
              disabled={fuelCount <= MIN_FUEL}
              className="w-20 h-20 bg-sky-500 text-white font-extrabold text-4xl rounded-2xl cursor-pointer hover:bg-sky-600 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap">
              −
            </button>
            <span className="text-sm font-extrabold text-sky-600 text-center">Bớt củi<br />giảm lửa</span>
          </div>
          <div className="flex flex-col items-center gap-1 px-4">
            <div className="text-2xl font-extrabold" style={{ color: heatColor }}>{fuelCount}</div>
            <span className="text-sm text-gray-500 font-bold">thanh củi</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => setFuelCount(v => Math.min(MAX_FUEL, v + 1))}
              disabled={fuelCount >= MAX_FUEL}
              className="w-20 h-20 bg-red-500 text-white font-extrabold text-4xl rounded-2xl cursor-pointer hover:bg-red-600 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap">
              +
            </button>
            <span className="text-sm font-extrabold text-red-600 text-center">Thêm củi<br />tăng lửa</span>
          </div>
        </div>
      </div>

      {/* Heat gauge */}
      <div className="w-full max-w-sm">
        <div className="relative w-full h-8 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to right, #60a5fa 0%, #22c55e 30%, #22c55e 65%, #f59e0b 80%, #ef4444 100%)' }}>
          <div className="absolute top-0 bottom-0 w-4 rounded-full bg-white border-2 border-gray-400 shadow transition-all duration-200" style={{ left: `calc(${Math.min(97, heat)}% - 8px)` }} />
        </div>
        <div className="flex justify-between text-sm mt-1 font-extrabold">
          <span className="text-sky-500">❄️ Lạnh</span>
          <span style={{ color: heatColor }} className="font-extrabold">
            {heatZone === 'perfect' ? '✅ VỪA ĐỦ!' : heatZone === 'hot' ? '🔥 QUÁ NÓNG!' : '❄️ QUÁ LẠNH!'}
          </span>
          <span className="text-red-500">🔥 Nóng</span>
        </div>
      </div>

      {/* Cook progress */}
      <div className="w-full max-w-sm">
        <div className="flex justify-between text-sm mb-2 font-extrabold">
          <span className="text-amber-600">🍚 Cơm đang chín</span>
          <span className="text-amber-700">{Math.round(cookProgress)}%</span>
        </div>
        <div className="w-full h-5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-300 to-orange-500 rounded-full transition-all" style={{ width: `${cookProgress}%` }} />
        </div>
      </div>

      {done && <p className="text-2xl font-extrabold text-green-600">✅ Cơm lam chín đều rồi!</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 9: XOAY ỐNG CƠM
// ══════════════════════════════════════════════════════
export function Round7XoayOng({ onComplete }: RoundProps) {
  const [rotation, setRotation] = useState(0);
  const [totalRotation, setTotalRotation] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [done, setDone] = useState(false);
  const [burnSide, setBurnSide] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [arrowDir, setArrowDir] = useState(1);
  const lastXRef = useRef<number | null>(null);
  const tubeRef = useRef<HTMLDivElement>(null);
  const TARGET_ROTATION = 720;
  const MIN_ROTATION = 400;

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => {
      setTimeLeft(v => {
        if (v <= 1) {
          clearInterval(t);
          const success = totalRotation >= MIN_ROTATION;
          if (!success) setBurnSide(true);
          setDone(true);
          setTimeout(() => onComplete(success, success ? 100 : 30), 1000);
          return 0;
        }
        return v - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [done, totalRotation, onComplete]);

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => {
      setArrowDir(d => (Math.random() > 0.4 ? d : d * -1));
    }, 4000);
    return () => clearInterval(t);
  }, [done]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (done) return;
    setIsDragging(true);
    lastXRef.current = e.clientX;
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || done || lastXRef.current === null) return;
    const dx = e.clientX - lastXRef.current;
    if (Math.abs(dx) > 2) {
      const dir = dx > 0 ? 1 : -1;
      const speed = Math.min(Math.abs(dx), 20);
      setRotation(v => v + dir * speed * 0.5);
      setTotalRotation(v => {
        const next = v + speed * 0.5;
        if (next >= TARGET_ROTATION) {
          setDone(true);
          setTimeout(() => onComplete(true, 100), 600);
        }
        return next;
      });
      lastXRef.current = e.clientX;
    }
  }, [isDragging, done, onComplete]);

  const handleMouseUp = () => {
    setIsDragging(false);
    lastXRef.current = null;
  };

  return (
    <div className="flex flex-col items-center gap-5 p-6 bg-yellow-50 rounded-3xl min-h-80 select-none"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}>
      <h3 className="text-2xl font-extrabold text-yellow-700 text-center">🔄 Xoay ống cơm!</h3>
      <Instruction text="🖱️ Giữ chuột trên ống nứa và kéo theo hướng mũi tên!" sub="Xoay đều để cơm chín đều - không xoay thì một bên sẽ cháy!" />

      <div className={`text-2xl font-extrabold ${timeLeft <= 10 ? 'text-red-600 animate-pulse' : 'text-yellow-700'}`}>
        ⏱️ {timeLeft}s {timeLeft <= 5 ? '⚠️' : ''}
      </div>

      <div className="flex items-center gap-8 text-4xl font-extrabold">
        <span className={`transition-all duration-500 ${arrowDir < 0 ? 'text-yellow-500 scale-150 animate-pulse' : 'text-gray-200'}`}>←</span>
        <span className="text-lg text-gray-500 font-normal">Hướng xoay</span>
        <span className={`transition-all duration-500 ${arrowDir > 0 ? 'text-yellow-500 scale-150 animate-pulse' : 'text-gray-200'}`}>→</span>
      </div>

      <div className="relative flex items-center justify-center w-60 h-56">
        <div className="absolute bottom-0 flex gap-3">
          <span className="text-3xl animate-bounce">🔥</span>
          <span className="text-3xl animate-bounce" style={{ animationDelay: '0.2s' }}>🔥</span>
          <span className="text-3xl animate-bounce" style={{ animationDelay: '0.4s' }}>🔥</span>
        </div>
        <div
          ref={tubeRef}
          onMouseDown={handleMouseDown}
          className={`relative transition-none cursor-ew-resize ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} ${!done ? 'hover:scale-105' : ''}`}
          style={{ transform: `rotate(${rotation % 360}deg)` }}>
          {burnSide && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-7 bg-black rounded-full opacity-70 pointer-events-none" />
          )}
          <div className="w-14 h-40 rounded-xl border-2 border-amber-400 flex flex-col items-center justify-center overflow-visible"
            style={{ background: burnSide ? 'linear-gradient(180deg, #b45309 40%, #1f2937 100%)' : 'linear-gradient(180deg, #fbbf24, #92400e)' }}>
            <span className="text-3xl">🎍</span>
            {isDragging && <span className="text-sm text-white font-extrabold mt-1 opacity-80">↻</span>}
          </div>
        </div>
        {!done && (
          <>
            <button onClick={() => { setRotation(v => v - 36); setTotalRotation(v => v + 36); }}
              className="absolute left-0 w-12 h-12 flex items-center justify-center bg-yellow-400 rounded-full cursor-pointer hover:bg-yellow-500 active:scale-90 text-white font-extrabold text-xl whitespace-nowrap">←</button>
            <button onClick={() => { setRotation(v => v + 36); setTotalRotation(v => v + 36); }}
              className="absolute right-0 w-12 h-12 flex items-center justify-center bg-yellow-400 rounded-full cursor-pointer hover:bg-yellow-500 active:scale-90 text-white font-extrabold text-xl whitespace-nowrap">→</button>
          </>
        )}
      </div>

      {burnSide && (
        <div className="bg-red-100 border-2 border-red-300 rounded-2xl px-5 py-4 text-center">
          <p className="text-base font-extrabold text-red-700">🖤 Một bên cháy đen vì không xoay!</p>
          <p className="text-sm text-red-500 mt-1">Nhớ: xoay đều để cơm chín đều nhé!</p>
        </div>
      )}

      <div className="w-full max-w-sm">
        <div className="flex justify-between text-sm mb-2 font-bold">
          <span className="text-yellow-600">Đã xoay: {Math.round(totalRotation)}°</span>
          <span className={totalRotation >= MIN_ROTATION ? 'text-green-600 font-extrabold' : 'text-orange-500'}>
            {totalRotation >= MIN_ROTATION ? '✅ Đủ rồi!' : `Cần ${MIN_ROTATION}°`}
          </span>
        </div>
        <div className="w-full h-5 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full transition-all"
            style={{ width: `${Math.min(100, (totalRotation / TARGET_ROTATION) * 100)}%` }} />
        </div>
      </div>

      {done && !burnSide && <p className="text-2xl font-extrabold text-green-600">✅ Xoay đều rồi! Cơm chín ngon!</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CÔNG ĐOẠN 10: HOÀN THÀNH
// ══════════════════════════════════════════════════════
export function Round8HoanThanh({ onComplete }: RoundProps) {
  const [phase, setPhase] = useState<'adult-split' | 'reveal' | 'eat' | 'done'>('adult-split');
  const [adultDone, setAdultDone] = useState(false);

  useEffect(() => {
    if (phase === 'done') {
      setTimeout(() => onComplete(true, 120), 800);
    }
  }, [phase, onComplete]);

  if (!adultDone) {
    return (
      <div className="flex flex-col gap-5 p-6 bg-green-50 rounded-3xl min-h-80">
        <h3 className="text-2xl font-extrabold text-green-700 text-center">🎉 Gần xong rồi! Chẻ ống nứa!</h3>
        <p className="text-base text-green-600 text-center">Ống nứa đã chín vàng thơm! Cần người lớn chẻ ống để lấy cơm ra!</p>
        <div className="flex justify-center text-8xl animate-bounce">🎍</div>
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-center">
          <p className="text-base font-extrabold text-amber-700">⚠️ Dao và ống nóng! Phải nhờ người lớn chẻ!</p>
        </div>
        <AdultHelp
          dangerousTask="Chẻ ống nứa nóng bằng dao"
          adultAction="Người lớn đang cẩn thận chẻ ống nứa nóng..."
          childSafeTask="Em đứng xa và chờ cơm lam nguội nhé!"
          adultEmoji="👩‍🍳"
          onAdultDone={() => setAdultDone(true)}
        />
      </div>
    );
  }

  if (phase === 'adult-split') {
    return (
      <div className="flex flex-col items-center gap-6 p-7 bg-green-50 rounded-3xl min-h-80">
        <h3 className="text-2xl font-extrabold text-green-700 text-center">🎉 Ống cơm đã được chẻ!</h3>
        <div className="text-8xl animate-pulse">🎍✨</div>
        <p className="text-lg font-extrabold text-green-600 text-center">Cơm lam vàng thơm đang hiện ra...</p>
        <button onClick={() => setPhase('reveal')} className="px-10 py-5 bg-green-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-green-600 whitespace-nowrap">
          🎉 Xem cơm lam!
        </button>
      </div>
    );
  }

  if (phase === 'reveal') {
    return (
      <div className="flex flex-col items-center gap-5 p-6 bg-amber-50 rounded-3xl min-h-80">
        <h3 className="text-3xl font-extrabold text-amber-700 text-center">🍚 Cơm lam chín vàng thơm!</h3>
        <div className="relative flex flex-col items-center">
          <div className="flex gap-1 text-sm animate-bounce mb-2 opacity-60">
            <span>〰️</span><span>〰️</span><span>〰️</span>
          </div>
          <div className="w-40 h-24 rounded-2xl bg-gradient-to-b from-amber-100 to-amber-300 border-2 border-amber-400 flex items-center justify-center">
            <span className="text-5xl">🍚</span>
          </div>
          <p className="text-base font-extrabold text-amber-600 mt-2">Vàng đẹp, thơm ngon!</p>
        </div>
        <div className="bg-white rounded-2xl p-4 w-full max-w-md">
          <p className="text-base font-extrabold text-green-700 text-center mb-3">✅ Em đã làm đúng tất cả!</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { emoji: '🙌', text: 'Rửa tay sạch' }, { emoji: '🎋', text: 'Dụng cụ sạch' },
              { emoji: '🍚', text: 'Gạo vo sạch' }, { emoji: '🔥', text: 'Lửa vừa đủ' },
              { emoji: '🔄', text: 'Xoay đều' }, { emoji: '👨‍👩‍👧', text: 'Người lớn giúp' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-green-50 rounded-xl p-3 text-base font-bold text-green-700">
                <span className="text-xl">{item.emoji}</span><span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <button onClick={() => setPhase('eat')} className="px-10 py-5 bg-amber-500 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:bg-amber-600 whitespace-nowrap">
          😋 Thưởng thức cơm lam!
        </button>
      </div>
    );
  }

  if (phase === 'eat') {
    return (
      <div className="flex flex-col items-center gap-5 p-7 bg-green-50 rounded-3xl min-h-80">
        <h3 className="text-3xl font-extrabold text-green-700 text-center">🎊 Cơm lam ngon lắm!</h3>
        <div className="text-8xl animate-bounce">😋</div>
        <div className="bg-green-100 border-2 border-green-300 rounded-2xl p-5 text-center">
          <p className="text-xl font-extrabold text-green-800">🎉 Ăn chín uống sôi!</p>
          <p className="text-base font-bold text-green-700 mt-2">Cơm chín kỹ = không có vi khuẩn = không đau bụng!</p>
        </div>
        <div className="flex items-center gap-4 bg-white rounded-2xl p-4 w-full max-w-sm">
          <span className="text-3xl">🦠</span>
          <div className="flex-1">
            <p className="text-base font-extrabold text-gray-600">Vi khuẩn:</p>
            <p className="text-base font-extrabold text-green-600">❌ Đã bị tiêu diệt hết!</p>
          </div>
          <span className="text-3xl">✅</span>
        </div>
        <button onClick={() => setPhase('done')} className="px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-400 text-white font-extrabold text-xl rounded-2xl cursor-pointer hover:opacity-90 whitespace-nowrap">
          🏆 Hoàn thành trò chơi!
        </button>
      </div>
    );
  }

  return <div className="flex items-center justify-center min-h-80"><div className="text-5xl animate-spin">🎉</div></div>;
}
